import { useCallback, useSyncExternalStore } from 'react';

export type Appearance = 'light' | 'dark';

const COOKIE_NAME = 'appearance';

// The cookie is the single source of truth: the server reads it to render the
// `dark` class, and anything other than "dark" (missing, legacy "system") is light.
const readAppearance = (): Appearance => {
    if (typeof document === 'undefined') {
        return 'light';
    }

    const match = document.cookie.match(
        new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`),
    );

    return match?.[1] === 'dark' ? 'dark' : 'light';
};

const setCookie = (value: Appearance, days = 365) => {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${COOKIE_NAME}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const applyTheme = (appearance: Appearance) => {
    const isDark = appearance === 'dark';

    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
};

// Every component using the hook (header toggle, settings tabs, toaster) shares
// one value, so they all update together.
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

export function initializeTheme() {
    applyTheme(readAppearance());
}

export function useAppearance() {
    const appearance = useSyncExternalStore(
        subscribe,
        readAppearance,
        (): Appearance => 'light',
    );

    const updateAppearance = useCallback((mode: Appearance) => {
        setCookie(mode);
        applyTheme(mode);
        listeners.forEach((listener) => listener());
    }, []);

    const toggleAppearance = useCallback(() => {
        updateAppearance(readAppearance() === 'dark' ? 'light' : 'dark');
    }, [updateAppearance]);

    return { appearance, updateAppearance, toggleAppearance } as const;
}
